/**
 * Subtitles — displays YouTube captions with live highlighting
 * synced to the player, auto-scroll, and seek-on-click.
 *
 * On desktop, a translation can be read side by side with the English
 * (?view=side-by-side), rows aligned on their timestamps.
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import type { SubtitlesConfig, Cue } from '../definitions/types';
import useCaptions from '../hooks/useCaptions';
import useMediaQuery from '../hooks/useMediaQuery';
import { alignCues, baseLang, findActiveIndex, formatTimestamp } from '../utils/captions';
import LanguageDropdown from './LanguageDropdown';
import style from '../style.module.css';

interface Props {
  config: SubtitlesConfig;
}

const Subtitles = ({ config }: Props) => {
  const { languages, ajaxUrl, videoId } = config;
  const { currentLang, cues, loading, switchLang } = useCaptions(config);
  const [activeIndex, setActiveIndex] = useState(-1);

  // side by side: desktop only, and only for a translation with English available
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const englishCode = languages.find((l) => baseLang(l.lang_code) === 'en')?.lang_code;
  const [sideBySide, setSideBySide] = useState(
    () => new URLSearchParams(window.location.search).get('view') === 'side-by-side',
  );
  const [englishCues, setEnglishCues] = useState<Cue[] | null>(null);
  const canCompare = isDesktop && !!englishCode && baseLang(currentLang) !== 'en';
  const showBoth = sideBySide && canCompare;

  useEffect(() => {
    if (!showBoth || englishCues || !englishCode) return;
    fetch(`${ajaxUrl}?action=kft_yt_captions&video_id=${videoId}&lang=${englishCode}`)
      .then((r) => r.json())
      .then((res: { success: boolean; data: Cue[] }) => setEnglishCues(res.success ? res.data ?? [] : []))
      .catch(() => setEnglishCues([]));
  }, [showBoth, englishCues, englishCode, ajaxUrl, videoId]);

  const englishRows = useMemo(
    () => (showBoth && englishCues ? alignCues(cues, englishCues) : null),
    [showBoth, englishCues, cues],
  );

  const toggleSideBySide = () => {
    const next = !sideBySide;
    setSideBySide(next);
    // keep the reading mode in the address, so it can be linked to
    const url = new URL(window.location.href);
    if (next) url.searchParams.set('view', 'side-by-side');
    else url.searchParams.delete('view');
    window.history.replaceState(null, '', url);
  };

  const langName = (code: string) =>
    languages.find((l) => l.lang_code === code)?.lang_name ?? code;

  const activeRowRef = useRef<HTMLTableRowElement | null>(null);
  const userScrolledRef = useRef(false);
  const userScrollTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Highlight loop — sync active cue with player time
  useEffect(() => {
    if (cues.length === 0) return;

    const interval = setInterval(() => {
      const player = window.kftPlayer;
      if (!player) return;

      const time = player.getCurrentTime();
      setActiveIndex(findActiveIndex(cues, time));
    }, 250);

    return () => clearInterval(interval);
  }, [cues]);

  // Auto-scroll to active row (media-page.js knows where the sticky player sits)
  useEffect(() => {
    if (activeIndex < 0 || userScrolledRef.current || !activeRowRef.current) return;
    window.kftRevealRow?.(activeRowRef.current);
  }, [activeIndex]);

  // User scroll detection — pause auto-scroll for 3s
  useEffect(() => {
    const onScroll = () => {
      userScrolledRef.current = true;
      clearTimeout(userScrollTimerRef.current);
      userScrollTimerRef.current = setTimeout(() => {
        userScrolledRef.current = false;
      }, 3000);
    };

    window.addEventListener('wheel', onScroll, { passive: true });
    window.addEventListener('touchmove', onScroll, { passive: true });
    return () => {
      window.removeEventListener('wheel', onScroll);
      window.removeEventListener('touchmove', onScroll);
      clearTimeout(userScrollTimerRef.current);
    };
  }, []);

  const handleSeek = (cue: Cue) => {
    const player = window.kftPlayer;
    if (!player) return;
    player.seekTo(cue.start);
    player.play();
  };

  const handleKeyDown = (e: React.KeyboardEvent, cue: Cue) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleSeek(cue);
    }
  };

  if (languages.length === 0) {
    return null;
  }

  return (
    <div>
      <div className={style.header}>
        <div>
          <span className={style.label}>Subtitles available in:</span>
          {languages.length > 1 ? (
            <LanguageDropdown
              languages={languages}
              currentLang={currentLang}
              onSelectLang={switchLang}
            />
          ) : (
            <strong>{languages[0].lang_name.toUpperCase()}</strong>
          )}
        </div>
        {englishCode && languages.length > 1 && (
          <button
            type="button"
            role="switch"
            aria-checked={showBoth}
            className={style.switch}
            disabled={!canCompare}
            title={canCompare ? undefined : 'Choose a translation to compare it with the English'}
            onClick={toggleSideBySide}
          >
            <span className={style.switchTrack} aria-hidden="true" />
            Side by side with English
          </button>
        )}
      </div>

      {languages.length > 1 && (
        <p className={style.note}>
          <strong className={style.noteLabel}>NOTE:</strong> Changing the subtitle language here only updates the transcript on this page.
          To change subtitles on the video itself, use the settings in the video player.
        </p>
      )}

      <hr className={style.divider} />

      {loading ? (
        <div className={style.loading}>Loading subtitles&hellip;</div>
      ) : (
        <table className={`${style.table} ${englishRows ? style.tableBoth : ''}`}>
          {englishRows && (
            <thead>
              <tr>
                <th />
                <th className={style.colHead} lang="en">English</th>
                <th className={style.colHead} lang={currentLang}>{langName(currentLang)}</th>
              </tr>
            </thead>
          )}
          <tbody>
            {cues.map((cue, i) => {
              const isActive = i === activeIndex;
              return (
                <tr
                  key={`${currentLang}-${i}`}
                  ref={isActive ? activeRowRef : null}
                  className={`${style.row} ${isActive ? style.active : ''}`}
                  tabIndex={0}
                  role="button"
                  onClick={() => handleSeek(cue)}
                  onKeyDown={e => handleKeyDown(e, cue)}
                >
                  <td className={style.time}>
                    <span className={style.seekLink}>{formatTimestamp(cue.start)}</span>
                  </td>
                  {englishRows && (
                    <td className={`${style.text} ${style.textEnglish}`} lang="en">{englishRows[i]}</td>
                  )}
                  <td className={style.text} lang={currentLang}>{cue.text}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default Subtitles;
